import React, { useState, useEffect } from "react";
import axios from "axios";
import { uploadFile, getFileUrl } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUT = {
  valide: { label: "Valide", cls: "bg-emerald-100 text-emerald-800" },
  bientot: { label: "Bientot expire", cls: "bg-amber-100 text-amber-800" },
  perime: { label: "Perime", cls: "bg-red-100 text-red-800" },
  sans_date: { label: "Sans date", cls: "bg-gray-100 text-gray-700" },
  manquant: { label: "Manquant", cls: "bg-gray-200 text-gray-700" },
};

export default function DocumentsConformitePage() {
  const [data, setData] = useState({ requis: [], lignes: [], n_food_trucks: 0, n_bloquants: 0 });
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // {ft_id, ft_nom, type, label}
  const [file, setFile] = useState(null);
  const [dateExp, setDateExp] = useState("");
  const [sending, setSending] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/documents-conformite`, auth());
      setData(r.data || { requis: [], lignes: [] });
    } catch (e) {
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const envoyer = async () => {
    if (!file) { toast.error("Choisissez un fichier"); return; }
    setSending(true);
    try {
      const up = await uploadFile(file);
      const d = (up && up.data) || {};
      const url = d.url || getFileUrl(d.file_id || d.id || d.fileId);
      await axios.post(`${API_URL}/food-trucks/${target.ft_id}/documents`, {
        type: target.type,
        nom: target.label || target.type,
        url: url,
        date_expiration: dateExp || null,
        description: "Depose par l'administration",
      }, auth());
      toast.success("Document importe pour " + target.ft_nom);
      setTarget(null); setFile(null); setDateExp("");
      await load();
    } catch (e) {
      toast.error("Erreur: " + (e?.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  const requis = data.requis || [];
  const rows = data.lignes || [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Suivi des documents</h1>
        <p className="text-sm text-gray-500">Statut des pieces obligatoires. Cliquez sur une case pour importer le document a la place du food truck.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border p-3"><div className="text-2xl font-bold">{data.n_food_trucks || 0}</div><div className="text-sm text-gray-500">Food trucks</div></div>
        <div className="rounded-lg border p-3"><div className="text-2xl font-bold text-red-600">{data.n_bloquants || 0}</div><div className="text-sm text-gray-500">Avec piece perimee</div></div>
      </div>

      {target && (
        <Card className="border-primary">
          <CardHeader><CardTitle className="text-base">Importer : {target.label} — {target.ft_nom}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} className="block w-full text-sm" />
            <div>
              <label className="text-xs text-gray-500">Date d'expiration (optionnel)</label>
              <input type="date" value={dateExp} onChange={(e) => setDateExp(e.target.value)} className="mt-1 block border rounded-md h-10 px-3" />
            </div>
            <div className="flex gap-2">
              <Button onClick={envoyer} disabled={sending}>{sending ? "Envoi..." : "Importer le document"}</Button>
              <Button variant="outline" onClick={() => { setTarget(null); setFile(null); setDateExp(""); }}>Annuler</Button>
            </div>
            <p className="text-xs text-gray-500">Import par l'administration : le compte du food truck n'est pas mis en attente de validation.</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Statut des pieces</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">Food truck</th>
                {requis.map((r) => (<th key={r.type} className="py-2 pr-3">{r.label}</th>))}
              </tr>
            </thead>
            <tbody>
              {rows.map((l, i) => (
                <tr key={i} className={"border-b " + (l.bloquant ? "bg-red-50" : "hover:bg-gray-50")}>
                  <td className="py-2 pr-3 font-medium">{l.nom}</td>
                  {requis.map((r) => {
                    const s = (l.statuts && l.statuts[r.type]) || {};
                    const st = STATUT[s.statut] || STATUT.manquant;
                    return (
                      <td key={r.type} className="py-2 pr-3">
                        <span className={"inline-block px-2 py-0.5 rounded text-xs " + st.cls}>{st.label}</span>
                        {s.date_expiration && <div className="text-xs text-gray-400">{String(s.date_expiration).slice(0, 10)}</div>}
                        <button
                          onClick={() => { setTarget({ ft_id: l.food_truck_id, ft_nom: l.nom, type: r.type, label: r.label }); setFile(null); setDateExp(""); }}
                          className="mt-1 text-xs text-primary underline"
                        >Importer</button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {rows.length === 0 && !loading && (<tr><td colSpan={requis.length + 1} className="py-4 text-center text-gray-400">Aucun food truck.</td></tr>)}
              {loading && (<tr><td colSpan={requis.length + 1} className="py-4 text-center text-gray-400">Chargement...</td></tr>)}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
